/**
 * The Screening Log tab — what the Make screening-rejection automation is doing.
 *
 * Three things, from the events Make reports via the /integrations screening-log
 * door:
 *   1. Archived   — how many candidates were archived, counted by role
 *   2. Emails sent — how many rejection emails went out, counted by role
 *   3. Failed      — full detail for every rejection that did NOT go out, with a
 *                    link to the candidate's Lever profile
 *
 * Counts are computed here over the events the server returns (up to its limit);
 * if screening volume ever outgrows that, move the aggregation server-side.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../shared';
import { fetchScreeningLog, type ScreeningEvent } from '../services/taOutboundApi';

const REASON_LABEL: Record<string, string> = {
  'template-not-found': 'No template',
  'send-failed': 'Send failed',
  'no-email': 'No email address',
  other: 'Other',
};

const REASON_CLASS: Record<string, string> = {
  'template-not-found': 'bg-[#FCEEE2] text-[#854F0B]',
  'send-failed': 'bg-[#FCDFE4] text-[#791F1F]',
  'no-email': 'bg-gray-100 text-gray-600',
  other: 'bg-gray-100 text-gray-600',
};

const UNKNOWN_ROLE = 'Unknown role';
const leverUrl = (opportunityID: string) => `https://hire.lever.co/candidates/${opportunityID}`;

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-GB', {
        timeZone: 'Europe/Berlin',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/** Count events of one type, grouped by role, highest first. */
function countByRole(events: ScreeningEvent[], event: string): { role: string; count: number }[] {
  const map = new Map<string, number>();
  for (const e of events) {
    if (e.event !== event) continue;
    const role = e.roleTitle || UNKNOWN_ROLE;
    map.set(role, (map.get(role) ?? 0) + 1);
  }
  return [...map.entries()].map(([role, count]) => ({ role, count })).sort((a, b) => b.count - a.count);
}

/** A count headline plus its per-role breakdown. */
function CountSection({ title, blurb, rows }: { title: string; blurb: string; rows: { role: string; count: number }[] }) {
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return (
    <div className="rounded-xl ring-1 ring-gray-200 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <span className="text-2xl font-semibold tabular-nums text-gray-900">{total}</span>
      </div>
      <p className="mt-0.5 text-xs text-gray-500">{blurb}</p>
      {rows.length > 0 ? (
        <ul className="mt-3 divide-y divide-gray-100">
          {rows.map((r) => (
            <li key={r.role} className="flex items-center justify-between gap-3 py-1.5 text-sm">
              <span className="truncate text-gray-700" title={r.role}>{r.role}</span>
              <span className="shrink-0 tabular-nums text-gray-500">{r.count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs text-gray-400">Nothing yet.</p>
      )}
    </div>
  );
}

/**
 * `scoped` says the rest of the page is filtered to one person. This section
 * cannot be: it counts the rejection automation's own activity by role, and
 * carries no per-person field. It says so rather than looking filtered.
 */
export function ScreeningLogPanel({ scoped = false }: { scoped?: boolean } = {}) {
  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['ta-screening-log'],
    queryFn: fetchScreeningLog,
  });
  const events: ScreeningEvent[] = data ?? [];

  const archived = useMemo(() => countByRole(events, 'archived'), [events]);
  const sent = useMemo(() => countByRole(events, 'sent'), [events]);
  const failed = useMemo(() => events.filter((e) => e.event === 'failed'), [events]);

  return (
    <section className="mt-8">
      <header className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Screening Log</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            What the screening-rejection automation did: how many candidates were archived and how
            many rejection emails went out (by role), plus every rejection that failed to send.
          </p>
          {scoped && (
            <p className="mt-1 text-xs text-gray-400">
              Always shows the whole team — the automation records what it did per role, not per
              person.
            </p>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => void refetch()} disabled={isFetching}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </header>

      {error && (
        <p className="rounded-lg border border-[#F7C0CA] bg-[#FCDFE4] px-3 py-2 text-sm text-[#791F1F]">
          {error instanceof Error ? error.message : 'Could not load the screening log.'}
        </p>
      )}

      {isPending ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <CountSection title="Archived" blurb="Candidates archived in screening, by role." rows={archived} />
            <CountSection title="Emails sent" blurb="Rejection emails sent, by role." rows={sent} />
          </div>

          <div className="mt-6">
            <h3 className="mb-2 text-sm font-semibold text-gray-900">
              Failed to send{failed.length ? ` (${failed.length})` : ''}
            </h3>
            {failed.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-gray-500">
                No failed rejections. 🎉
              </p>
            ) : (
              <div className="overflow-x-auto rounded-xl ring-1 ring-gray-200">
                <table className="w-full min-w-[52rem] text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">When</th>
                      <th className="px-3 py-2 font-semibold">Candidate</th>
                      <th className="px-3 py-2 font-semibold">Role</th>
                      <th className="px-3 py-2 font-semibold">Why</th>
                      <th className="px-3 py-2 font-semibold">Template</th>
                      <th className="px-3 py-2 font-semibold">Sender</th>
                      <th className="px-3 py-2 font-semibold">Attempts</th>
                      <th className="px-3 py-2 font-semibold">Lever</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failed.map((r) => (
                      <tr key={`${r.opportunityID}-${r.occurredAt}`} className="border-t border-gray-100">
                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">{fmtWhen(r.occurredAt)}</td>
                        <td className="px-3 py-2">
                          <span className="font-medium text-gray-900">
                            {r.candidateName || r.candidateEmail || r.opportunityID}
                          </span>
                          {r.candidateEmail ? (
                            <span className="block text-xs text-gray-400">{r.candidateEmail}</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{r.roleTitle || '—'}</td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[11px] font-medium',
                              REASON_CLASS[r.reason ?? 'other'] || REASON_CLASS.other
                            )}
                          >
                            {REASON_LABEL[r.reason ?? 'other'] || r.reason}
                          </span>
                          {r.detail ? (
                            <span className="mt-0.5 block max-w-[14rem] truncate text-xs text-gray-400" title={r.detail}>
                              {r.detail}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {r.templateKey || '—'}
                          {r.language ? <span className="block text-xs text-gray-400">{r.language}</span> : null}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">{r.senderEmail || '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-gray-500">{r.attempts ?? '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <a
                            href={leverUrl(r.opportunityID)}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-800 hover:border-gray-500"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Open
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
